/**
 * Motif de correction OBLIGATOIRE de La Régie (mission A2 « AMENDEMENTS
 * 07-10 soir »). Quand un superviseur modifie le texte proposé (carte
 * parole OU carte action) avant de l'envoyer, un motif 1-tap s'intercale —
 * ce module porte les slugs stables + la logique PURE de gating (« le texte
 * a-t-il vraiment changé ? ») et de construction du `reason` envoyé au
 * backend (`edits.reason`, cf `RegieActionBar.tsx`).
 *
 * DISTINCT de `reasons.ts` (motifs de REFUS complet d'une proposition) et de
 * `timeWasterReasons.ts` (ban) : ici on motive une CORRECTION — le message
 * part quand même, juste modifié. Contrairement au motif Time Waster, le
 * texte libre d'« Autre » est OPTIONNEL (le superviseur peut valider sans
 * détailler) — cf `buildCorrectionReason`.
 *
 * Logique pure, sans dépendance React — testée dans
 * `__tests__/correctionReasons.test.ts`.
 */

export interface CorrectionReason {
  /** Slug stable envoyé au backend (`edits.reason`). NE JAMAIS renommer. */
  slug: string;
  /** Émoji d'accroche affiché sur le bouton (pattern demandé par la
   *  mission — grille de gros boutons, pas de raccourci clavier dédié ici :
   *  1/2/3/E/X/S/O sont déjà pris par `RegieActionBar`). */
  emoji: string;
  /** Libellé FR de repli (i18n `panels.regie.correctionReasons.<slug>`). */
  defaultLabel: string;
}

/** Slug du motif libre — seul cas où `reason` n'est pas le slug brut (cf
 *  `buildCorrectionReason`). Contrairement à `OTHER_TIME_WASTER_SLUG`, le
 *  texte qui l'accompagne est OPTIONNEL. */
export const OTHER_CORRECTION_REASON_SLUG = "other";

/** Les 4 presets + « Autre », dans l'ordre d'affichage (5 motifs gelés au
 *  plan lundi `PLAN_CYRANO_LA_REGIE_LUNDI_2026-07-13.md` §2.2 :
 *  off_topic / incoherent / invented / robotic_tone / other). Slugs
 *  load-bearing (contrat backend `edits.reason`) → figés par test. */
export const CORRECTION_REASONS: readonly CorrectionReason[] = [
  { slug: "off_topic", emoji: "🎯", defaultLabel: "À côté de la plaque" },
  { slug: "incoherent", emoji: "🌀", defaultLabel: "Ça ne veut rien dire" },
  { slug: "invented", emoji: "🧾", defaultLabel: "Elle invente" },
  { slug: "robotic_tone", emoji: "🤖", defaultLabel: "Robotique" },
] as const;

/**
 * True si le texte édité diffère réellement du texte d'origine (après trim)
 * — condition d'affichage de l'écran de motif (« pas de motif si le texte
 * n'a pas changé »).
 *
 * `editedMessageRaw` vide (après trim) veut toujours dire « garde tel quel »
 * (aucun `edits.message` ne part, cf `RegieActionBar.sendCorrected`) — donc
 * JAMAIS considéré comme une édition, quel que soit `originalMessage`
 * (cas carte parole : effacer la zone de correction revient au message
 * proposé, pas à un envoi vide).
 *
 * `originalMessage` est la référence : le texte exact du variant (`primary
 * .messageText`) pour une carte parole, ou `""` pour une carte action (qui
 * n'a pas de texte d'origine connu côté front — tout texte non vide y est
 * par définition une addition/correction).
 */
export function needsCorrectionReason(
  originalMessage: string,
  editedMessageRaw: string,
): boolean {
  const trimmedEdited = editedMessageRaw.trim();
  if (trimmedEdited.length === 0) return false;
  return trimmedEdited !== originalMessage.trim();
}

/**
 * Construit le `reason` EXACT envoyé au backend (`edits.reason`) à partir du
 * preset sélectionné (+ texte libre optionnel si « Autre ») :
 *   - preset connu (≠ other) → le slug BRUT, tel quel (`otherText` ignoré).
 *   - `other` → `"other: <texte>"` si un texte non vide est fourni, sinon
 *     `"other"` seul (texte OPTIONNEL, contrairement à Time Waster).
 *   - `slug` `null`/inconnu → `null` (aucun motif sélectionné — le caller
 *     garde alors l'étape affichée, rien à envoyer).
 */
export function buildCorrectionReason(
  slug: string | null,
  otherText: string,
): string | null {
  if (!slug) return null;
  if (slug === OTHER_CORRECTION_REASON_SLUG) {
    const trimmed = otherText.trim();
    return trimmed.length > 0 ? `${OTHER_CORRECTION_REASON_SLUG}: ${trimmed}` : OTHER_CORRECTION_REASON_SLUG;
  }
  const known = CORRECTION_REASONS.some((r) => r.slug === slug);
  return known ? slug : null;
}
